import { useRef, useState, useEffect } from 'react';
import Image from 'next/image';
import PropTypes from 'prop-types';
import { X } from 'react-feather';

export function ParsingText({ text }) {
    return (
        <div className="text-success-400 text-[10px] flex items-center whitespace-nowrap">
            {text}
            <span className="flex ml-0.5 space-x-0.5 relative top-0.5">
                <span className="w-0.5 h-0.5 bg-success-400 rounded-full animate-[blink_1.4s_infinite] [animation-delay:0s]"></span>
                <span className="w-0.5 h-0.5 bg-success-400 rounded-full animate-[blink_1.4s_infinite] [animation-delay:0.2s]"></span>
                <span className="w-0.5 h-0.5 bg-success-400 rounded-full animate-[blink_1.4s_infinite] [animation-delay:0.4s]"></span>
            </span>
            <style>{`
                @keyframes blink {
                    0%, 80%, 100% { opacity: 0.2; }
                    40% { opacity: 1; }
                }
            `}</style>
        </div>
    );
}

const InputBox = ({
    isError,
    errorMessage,
    className,
    variant,
    disabled,
    type,
    register,
    id,
    dbName,
    isLabel,
    labelFor,
    labelText,
    labelClass,
    placeholder,
    iconComponent,
    reference,
    bgColor,
    iconClass,
    inputRadius,
    inputBorder,
    errorClass,
    isCloseIcon = false,
    onClearSearch,
    placeholderColor,
    closeIconClass,
    autoFocus = false,
    onKeyDown,
    parentClassName,
    isCommandLine = false,
    plotLoader = false,
    loaderText,
    ...otherProps
}) => {
    const [textWidth, setTextWidth] = useState(0);
    const measureRef = useRef(null);

    useEffect(() => {
        if (measureRef.current) {
            measureRef.current.textContent = otherProps.value;
            const measuredWidth = measureRef.current.offsetWidth;
            const leftPadding = iconComponent ? 40 : 16;
            setTextWidth(measuredWidth + leftPadding);
        }
    }, [otherProps.value]);

    const inputDisabledClass = disabled
        ? 'cursor-not-allowed disabled:bg-gray-100 disabled:border-gray-300 disabled:placeholder:text-gray-500'
        : '';

    const inputBorderStyle = isError
        ? 'border-error-300 hover:border-error-300 focus:border-error-300'
        : `${inputBorder || 'border-transparent hover:border-primary-300 focus:border-primary-300'} ${isCommandLine && '!border-gray-500 focus:!border-success-600 focus:shadow-[0_0_4px_1px_rgba(44,186,147,0.9)]'}`;

    return (
        <div className={`relative ${parentClassName}`}>
            {isLabel && (
                <label
                    htmlFor={labelFor || id}
                    className={`text-neutral-900 text-base leading-6 font-normal block pb-2 ${labelClass}`}
                >
                    {labelText}
                </label>
            )}

            <div className={'relative flex items-center'}>
                {iconComponent && !plotLoader && (
                    <div className={`${iconClass} absolute left-3 text-gray-200`}>
                        {iconComponent}
                    </div>
                )}
                {plotLoader && (
                    <div className={`${iconClass} absolute left-2 text-gray-200`}>
                        <Image src="/images/plot_loader.gif" alt="deon" width="26" height="26" />
                    </div>
                )}
                {/* Invisible span for measuring text width */}
                <span ref={measureRef} className="absolute invisible whitespace-nowrap text-gray-100 text-base"></span>
                <input autoComplete="off"
                    id={id}
                    type={type || 'text'}
                    placeholder={placeholder}
                    ref={reference}
                    {...(register && { ...register(dbName) })}
                    onKeyDown={onKeyDown}
                    className={`
                    ${iconComponent ? 'pl-10' : 'pl-4'} border pr-4 h-10 w-full outline-none font-normal ${placeholderColor || 'placeholder:text-gray-200'} text-gray-100 appearance-none select-none
                    ${inputDisabledClass}
                    ${bgColor || 'bg-gray-500'}
                    ${inputRadius || 'rounded-md'}
                    ${inputBorderStyle}
                    ${className}
          `}
                    disabled={disabled}
                    {...otherProps}
                    aria-invalid={isError}
                />
                {loaderText && (
                    <div className="absolute top-1/2 -translate-y-1/2" style={{ left: `${textWidth + 24}px` }}>
                        <ParsingText text={loaderText} />
                    </div>
                )}
                {isCloseIcon && <X size={16} className={`text-basic-200 absolute right-3.5 stroke-[3px] ${closeIconClass}`} onClick={onClearSearch} />}
                {isError && (
                    <span
                        className={`text-error-300 pl-0.5 text-sm pt-2 font-normal leading-[21px] select-none ${errorClass}`}
                        role="alert"
                    >
                        {errorMessage}
                    </span>
                )}
            </div>
        </div>
    );
};

InputBox.defaultProps = {
    isError: false,
    className: '',
    variant: 'Default',
    disabled: false,
    isLabel: false,
    iconComponent: null,
    id: '',
    bgColor: ''
};

InputBox.propTypes = {
    id: PropTypes.string,
    dbName: PropTypes.string,
    type: PropTypes.string,
    placeholder: PropTypes.string,
    disableClass: PropTypes.string,
    baseClasses: PropTypes.string,
    className: PropTypes.string,
    variant: PropTypes.oneOf(['Default', 'Small', 'Large']),
    disabled: PropTypes.bool,
    isError: PropTypes.bool,
    errorMessage: PropTypes.string,
    register: PropTypes.func,
    isLabel: PropTypes.bool,
    labelText: PropTypes.string,
    labelFor: PropTypes.string,
    labelClass: PropTypes.string,
    iconComponent: PropTypes.node,
    bgColor: PropTypes.string,
    iconClass: PropTypes.string
};

export default InputBox;
